import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../../lib/api-client';

import { deveContinuarPolling } from './onboarding-diagnosis';
import { CHANNELS_KEY, channelHealthKey } from './use-channels';

import type {
  OnboardingAttemptWire,
  OnboardingAvailabilityWire,
  OnboardingPathWire,
  OnboardingStartWire,
  PopupResultWire,
  RemoteHealthWire,
} from './types';

/**
 * Hooks do onboarding (W2). A tentativa em andamento é lida por **polling curto** (2 s): o
 * pipeline roda em segundos e o WS não tem evento próprio ainda (spec §6.2 deixa os dois em
 * aberto). Quando a tentativa para de andar, o polling para sozinho.
 */

export const ONBOARDING_KEY = [...CHANNELS_KEY, 'onboarding'] as const;
const AVAILABILITY_KEY = [...ONBOARDING_KEY, 'availability'] as const;
const CURRENT_KEY = [...ONBOARDING_KEY, 'current'] as const;

export function useOnboardingAvailability(
  enabled: boolean,
): ReturnType<typeof useQuery<OnboardingAvailabilityWire>> {
  return useQuery({
    queryKey: AVAILABILITY_KEY,
    queryFn: () => api.getOnboardingAvailability(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCurrentAttempt(
  enabled: boolean,
): ReturnType<typeof useQuery<OnboardingAttemptWire | null>> {
  return useQuery({
    queryKey: CURRENT_KEY,
    queryFn: () => api.getCurrentOnboardingAttempt(),
    enabled,
    staleTime: 0,
    refetchInterval: (q) => (deveContinuarPolling(q.state.data ?? null) ? 2_000 : false),
  });
}

type QC = ReturnType<typeof useQueryClient>;

/** Toda mutação do onboarding mexe na tentativa E na lista de canais (o passo 1 cria o canal). */
function invalidarTudo(qc: QC, channelId?: string | null): void {
  void qc.invalidateQueries({ queryKey: CURRENT_KEY });
  void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
  if (channelId !== undefined && channelId !== null) {
    void qc.invalidateQueries({ queryKey: channelHealthKey(channelId) });
  }
}

export function useStartAttempt(): ReturnType<
  typeof useMutation<
    OnboardingStartWire,
    Error,
    { path: OnboardingPathWire; history?: { import: boolean; retentionMonths: 6 | 12 | 24 | 60 } }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b) => api.startOnboardingAttempt(b),
    onSuccess: () => {
      invalidarTudo(qc);
    },
  });
}

export function usePostPopupResult(): ReturnType<
  typeof useMutation<OnboardingAttemptWire, Error, { id: string; body: PopupResultWire }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => api.postOnboardingResult(id, body),
    onSuccess: (a) => {
      invalidarTudo(qc, a.channelConfigId);
    },
  });
}

export function useResumeAttempt(): ReturnType<
  typeof useMutation<OnboardingAttemptWire, Error, { id: string; pin?: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pin }) => api.resumeOnboardingAttempt(id, pin),
    onSuccess: (a) => {
      invalidarTudo(qc, a.channelConfigId);
    },
  });
}

export function useCancelAttempt(): ReturnType<
  typeof useMutation<OnboardingAttemptWire, Error, string>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.cancelOnboardingAttempt(id),
    onSuccess: () => {
      invalidarTudo(qc);
    },
  });
}

export function useDisconnectWhatsApp(): ReturnType<
  typeof useMutation<{ unsubscribed: boolean }, Error, void>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.disconnectWhatsApp(),
    onSuccess: () => {
      invalidarTudo(qc);
    },
  });
}

export function useRefreshRemoteHealth(): ReturnType<
  typeof useMutation<RemoteHealthWire, Error, string>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.refreshRemoteHealth(id),
    onSuccess: (_r, id) => {
      invalidarTudo(qc, id);
    },
  });
}

export function useResubscribe(): ReturnType<typeof useMutation<RemoteHealthWire, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.resubscribeChannel(id),
    onSuccess: (_r, id) => {
      invalidarTudo(qc, id);
    },
  });
}
